/**
 * EarningsBar — Motivierende Verdienst-Anzeige für Gutachter.
 *
 * Zeigt oben auf dem Dashboard die Brutto-Honorarsumme aller abgeschlossenen
 * Akten (Status released / sent_to_customer). Hero-Counter (animiert)
 * = aktueller Monat, daneben kleine Sub-Stats: Heute · Jahr · Gesamt.
 *
 * Rendert NUR für die Rolle "gutachter".
 */
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Coins } from "lucide-react";

const fmtEur = (v, opts = {}) =>
  Number(v || 0).toLocaleString("de-DE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...opts,
  });

// easeOutCubic — sanftes Auslaufen wie ein Tachometer
const easeOut = (t) => 1 - Math.pow(1 - t, 3);

function useCountUp(target, durationMs = 1400) {
  const [value, setValue] = useState(0);
  const fromRef = useRef(0);
  const rafRef = useRef(null);

  useEffect(() => {
    const from = fromRef.current;
    const to = Number(target || 0);
    if (from === to) return;
    const start = performance.now();
    const tick = (now) => {
      const elapsed = now - start;
      const t = Math.min(1, elapsed / durationMs);
      const eased = easeOut(t);
      const current = from + (to - from) * eased;
      setValue(current);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        fromRef.current = to;
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [target, durationMs]);

  return value;
}

export default function EarningsBar() {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const monthAnim = useCountUp(data?.month || 0, 1600);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: d } = await api.get("/dashboard/earnings");
        if (alive) setData(d);
      } catch (e) {
        // Stillschweigend ignorieren — die Bar ist nicht kritisch fürs Dashboard
        if (alive) setData(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (loading) {
    return (
      <div
        className="mb-6 rounded-2xl brand-gradient-bg p-6 h-[130px] animate-pulse"
        data-testid="earnings-bar-loading"
      />
    );
  }

  if (!data) return null;

  const monthLabel = (data.as_of
    ? new Date(data.as_of)
    : new Date()
  ).toLocaleDateString(i18n.resolvedLanguage || "de", { month: "long", year: "numeric" });

  return (
    <div
      className="mb-6 relative overflow-hidden rounded-2xl brand-gradient-bg p-6 shadow-lg"
      data-testid="earnings-bar"
    >
      {/* Subtle pattern overlay für Motivations-Feel */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.07]" style={{
        backgroundImage: "radial-gradient(circle at 25% 30%, white 1px, transparent 1px), radial-gradient(circle at 75% 70%, white 1px, transparent 1px)",
        backgroundSize: "32px 32px, 48px 48px",
      }} />

      <div className="relative">
        {/* Hero-Counter: aktueller Monat */}
        <div data-testid="earnings-hero">
          <div className="flex items-center gap-2 mb-2">
            <Coins className="h-4 w-4 text-white/80" />
            <span className="text-[11px] uppercase tracking-[0.22em] font-bold text-white/90">
              {t("dashboard.earnings_title")} · {monthLabel}
            </span>
          </div>
          <div
            className="font-display font-bold text-white text-5xl sm:text-6xl tracking-tight tabular-nums leading-none"
            data-testid="earnings-month-hero"
          >
            {fmtEur(monthAnim)}
          </div>
        </div>
      </div>
    </div>
  );
}
